import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ClipboardCopy, Download, Loader2, LogOut, Trash2, Users } from 'lucide-react';
import { clearToken, deleteRsvp, downloadRegistrationsCsv, getRegistrationEmailStatus, getToken, listRegistrations, listRsvps, me, sendWelcomeEmail } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';
import { RsvpReminderPanel } from '../components/admin/RsvpReminderPanel';
import { PopcornSettingsPanel } from '../components/admin/PopcornSettingsPanel';
import { WelcomeEmailCell } from '../components/admin/WelcomeEmailCell';

const FUNDRAISERS = [{ slug: 'roller-skating-2026-09-25', label: 'Roller Skating Night · Fri Sep 25' }];
const navLink = 'text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20';
const th = 'text-left text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 px-3 py-2';
const td = 'px-3 py-2 text-sm text-[#0a1f44] align-top';

const useStaff = () => {
  const [state, setState] = useState({ authOk: null, profile: null });
  useEffect(() => {
    if (!getToken()) { setState({ authOk: false, profile: null }); return; }
    me().then((p) => setState({ authOk: true, profile: p })).catch(() => { clearToken(); setState({ authOk: false, profile: null }); });
  }, []);
  return state;
};

const Header = ({ title }) => (
  <header className="bg-[#0a1f44] text-white">
    <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-4"><img src={iceLogo} alt="ICE" className="h-10 w-auto" /><div><div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Admin Console</div><div className="font-display text-2xl leading-none mt-1">{title}</div></div></div>
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/admin/announcements" className={navLink}>Announcements</Link>
        <Link to="/admin/registrations" data-testid="admin-nav-registrations" className={navLink}>Registrations</Link>
        <Link to="/admin/fundraisers" data-testid="admin-nav-fundraisers" className={navLink}>RSVPs</Link>
            <Link to="/admin/weekly-text" data-testid="admin-nav-weekly-text" className={navLink}>Weekly Text</Link>
        <Link to="/admin/account" data-testid="admin-nav-account" className={navLink}>Change Password</Link>
        <button onClick={() => { clearToken(); window.location.href = '/admin/login'; }} className={`inline-flex items-center gap-1 ${navLink}`}><LogOut className="w-3 h-3" /> Sign Out</button>
      </div>
    </div>
  </header>
);

export default function AdminFundraisers() {
  const { toast } = useToast();
  const { authOk } = useStaff();
  const [slug, setSlug] = useState(FUNDRAISERS[0].slug);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listRsvps(slug)); } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || e.message }); } finally { setLoading(false); }
  }, [slug, toast]);
  useEffect(() => { if (authOk) load(); }, [authOk, load]);

  const people = rows.reduce((sum, r) => sum + r.headcount, 0);
  const copyList = async () => {
    const text = [`${FUNDRAISERS.find((f) => f.slug === slug)?.label} — ${rows.length} families · ${people} people`, ...rows.map((r) => `${r.name} (${r.headcount})${r.phone ? ` · ${r.phone}` : ''}${r.note ? ` · ${r.note}` : ''}`)].join('\n');
    try { await navigator.clipboard.writeText(text); toast({ title: 'RSVP list copied' }); } catch { toast({ title: 'Could not copy', description: text.slice(0, 200) }); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name}'s RSVP?`)) return;
    try { await deleteRsvp(slug, row.id); await load(); } catch (e) { toast({ title: 'Remove failed', description: e.response?.data?.detail || e.message }); }
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login?next=/admin/fundraisers" replace />;

  return (
    <div className="min-h-screen bg-[#f5f6f8]" data-testid="admin-fundraisers-page">
      <Header title="FUNDRAISER RSVPS" />
      <section className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        <div className="bg-white border border-[#0a1f44]/10 p-5 flex flex-wrap items-center gap-4">
          <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="rsvp-admin-select" className="border border-[#0a1f44]/20 px-3 py-2 text-sm">{FUNDRAISERS.map((f) => <option key={f.slug} value={f.slug}>{f.label}</option>)}</select>
          <div className="flex items-center gap-2 font-display text-2xl text-[#0a1f44]" data-testid="rsvp-admin-totals"><Users className="w-5 h-5 text-[#c8102e]" /> {rows.length} families · {people} people</div>
          <button type="button" onClick={copyList} data-testid="rsvp-admin-copy" className="ml-auto inline-flex items-center gap-2 bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2.5 transition-colors"><ClipboardCopy className="w-4 h-4" /> Copy List</button>
        </div>
        <div className="bg-white border border-[#0a1f44]/10 overflow-x-auto">
          {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0a1f44]/50" /></div> : (
            <table className="w-full" data-testid="rsvp-admin-table">
              <thead className="bg-[#f5f6f8]"><tr><th className={th}>Family</th><th className={th}>People</th><th className={th}>Phone</th><th className={th}>Note</th><th className={th}>When</th><th className={th} /></tr></thead>
              <tbody className="divide-y divide-[#0a1f44]/10">
                {rows.length === 0 && <tr><td colSpan={6} className={`${td} text-center text-[#0a1f44]/50 py-10`} data-testid="rsvp-admin-empty">No RSVPs yet.</td></tr>}
                {rows.map((r) => <tr key={r.id} data-testid={`rsvp-admin-row-${r.id}`}><td className={`${td} font-semibold`}>{r.name}</td><td className={td}>{r.headcount}</td><td className={td}>{r.phone || '—'}</td><td className={td}>{r.note || '—'}</td><td className={`${td} text-xs text-[#0a1f44]/60`}>{new Date(r.createdAt).toLocaleString()}</td><td className={td}><button type="button" onClick={() => remove(r)} data-testid={`rsvp-admin-delete-${r.id}`} className="text-[#c8102e] hover:text-[#e11d34]"><Trash2 className="w-4 h-4" /></button></td></tr>)}
              </tbody>
            </table>
          )}
        </div>
        <RsvpReminderPanel slug={slug} rows={rows} />
        <PopcornSettingsPanel />
      </section>
    </div>
  );
}

export function AdminRegistrations() {
  const { toast } = useToast();
  const { authOk, profile } = useStaff();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(null);

  useEffect(() => {
    if (!authOk) return;
    listRegistrations().then((data) => { setRows(data); setLoading(false); }).catch((e) => { setLoading(false); if (e.response?.status === 403) setForbidden(true); else toast({ title: 'Error', description: e.response?.data?.detail || e.message }); });
    getRegistrationEmailStatus().then((s) => setEmailConfigured(s.configured)).catch(() => setEmailConfigured(false));
  }, [authOk, toast]);

  const sendWelcome = async (row) => {
    try {
      const updated = await sendWelcomeEmail(row.id);
      setRows((current) => current.map((r) => (r.id === row.id ? updated : r)));
      toast({ title: updated.welcomeEmail.status === 'sent' ? 'Welcome email sent' : 'Email not sent', description: updated.welcomeEmail.status === 'sent' ? `Delivered to ${row.email}` : updated.welcomeEmail.error || updated.welcomeEmail.reason });
    } catch (e) { toast({ title: 'Send failed', description: e.response?.data?.detail || e.message }); }
  };

  const download = async () => {
    try {
      const blob = await downloadRegistrationsCsv();
      const url = URL.createObjectURL(blob);
      const link = Object.assign(document.createElement('a'), { href: url, download: `patriots-registrations-${new Date().toISOString().slice(0, 10)}.csv` });
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Export failed', description: e.response?.data?.detail || e.message }); }
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login?next=/admin/registrations" replace />;
  const byDivision = rows.reduce((acc, r) => ({ ...acc, [r.division || '—']: (acc[r.division || '—'] || 0) + 1 }), {});

  return (
    <div className="min-h-screen bg-[#f5f6f8]" data-testid="admin-registrations-page">
      <Header title="PAID REGISTRATIONS" />
      <section className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        {forbidden ? <div className="bg-white border-l-4 border-[#c8102e] p-6 text-sm text-[#0a1f44]" data-testid="registrations-forbidden">Only the primary admin can view registrations. Signed in as {profile?.email}.</div> : (
          <>
            <div className="bg-white border border-[#0a1f44]/10 p-5 flex flex-wrap items-center gap-4">
              <div className="font-display text-2xl text-[#0a1f44]" data-testid="registrations-total">{rows.length} paid · ${rows.reduce((s, r) => s + r.amount, 0).toFixed(2)}</div>
              <div className="flex gap-2 flex-wrap" data-testid="registrations-by-division">{Object.entries(byDivision).map(([d, n]) => <span key={d} className="bg-[#0a1f44] text-white px-2.5 py-1 font-heading uppercase tracking-widest text-[10px]">{d}: {n}</span>)}</div>
              <button type="button" onClick={download} data-testid="registrations-download-csv" className="ml-auto inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2.5 transition-colors"><Download className="w-4 h-4" /> Download CSV</button>
            </div>
            {emailConfigured === false && <div className="bg-[#fff7e6] border-l-4 border-[#f4b400] p-4 text-sm text-[#0a1f44]" data-testid="welcome-email-unconfigured">Welcome emails are ready but not yet active — the managed email service isn't configured in this environment yet. Once it is, every new paid registration gets a welcome note with practice details automatically.</div>}
            <div className="bg-white border border-[#0a1f44]/10 overflow-x-auto">
              {loading ? <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0a1f44]/50" /></div> : (
                <table className="w-full" data-testid="registrations-table">
                  <thead className="bg-[#f5f6f8]"><tr><th className={th}>Player</th><th className={th}>Division</th><th className={th}>DOB / Grade</th><th className={th}>Parent</th><th className={th}>Phone</th><th className={th}>Email</th><th className={th}>Paid</th><th className={th}>Welcome Email</th></tr></thead>
                  <tbody className="divide-y divide-[#0a1f44]/10">
                    {rows.length === 0 && <tr><td colSpan={8} className={`${td} text-center text-[#0a1f44]/50 py-10`} data-testid="registrations-empty">No paid registrations yet.</td></tr>}
                    {rows.map((r) => <tr key={r.id} data-testid={`registration-row-${r.id}`}><td className={`${td} font-semibold`}>{r.scholar}</td><td className={`${td} uppercase`}>{r.division}</td><td className={td}>{r.dob || '—'}{r.grade ? ` · Gr ${r.grade}` : ''}</td><td className={td}>{r.parent}</td><td className={td}><a href={`tel:${r.phone}`} className="underline">{r.phone}</a></td><td className={td}><a href={`mailto:${r.email}`} className="underline">{r.email}</a></td><td className={`${td} text-xs text-[#0a1f44]/60`}>${r.amount.toFixed(2)}<br />{r.paidAt ? new Date(r.paidAt).toLocaleDateString() : ''}</td><td className={td}><WelcomeEmailCell row={r} configured={emailConfigured} onSend={sendWelcome} /></td></tr>)}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
